import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/skeleton";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { format, subDays, eachDayOfInterval } from "date-fns";

export const AnalyticsDashboard = () => {
  const { data: pageViews, isLoading: loadingViews } = useQuery({
    queryKey: ["analytics-page-views"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("page_views")
        .select("*", { count: "exact", head: true });
      if (error) throw error;
      return count || 0;
    },
  });

  const { data: uniqueVisitors, isLoading: loadingVisitors } = useQuery({
    queryKey: ["analytics-unique-visitors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_views")
        .select("visitor_id")
        .not("visitor_id", "is", null);
      if (error) throw error;
      const unique = new Set(data.map((v) => v.visitor_id));
      return unique.size;
    },
  });

  const { data: projectViews, isLoading: loadingProjects } = useQuery({
    queryKey: ["analytics-project-views"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_views")
        .select("project_id, projects(project_name)")
        .not("project_id", "is", null);
      if (error) throw error;

      const counts: Record<string, { name: string; count: number }> = {};
      data.forEach((view) => {
        if (view.project_id) {
          if (!counts[view.project_id]) {
            counts[view.project_id] = {
              name: (view.projects as { project_name: string })?.project_name || "Unknown",
              count: 0,
            };
          }
          counts[view.project_id].count++;
        }
      });

      return Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 5);
    },
  });

  const { data: viewsOverTime, isLoading: loadingTimeline } = useQuery({
    queryKey: ["analytics-views-timeline"],
    queryFn: async () => {
      const thirtyDaysAgo = subDays(new Date(), 30);
      const { data, error } = await supabase
        .from("page_views")
        .select("created_at")
        .gte("created_at", thirtyDaysAgo.toISOString());
      if (error) throw error;

      const days = eachDayOfInterval({
        start: thirtyDaysAgo,
        end: new Date(),
      });

      const countsByDay: Record<string, number> = {};
      days.forEach((day) => {
        countsByDay[format(day, "yyyy-MM-dd")] = 0;
      });

      data.forEach((view) => {
        const day = format(new Date(view.created_at), "yyyy-MM-dd");
        if (countsByDay[day] !== undefined) {
          countsByDay[day]++;
        }
      });

      return Object.entries(countsByDay).map(([date, views]) => ({
        date: format(new Date(date), "MMM d"),
        views,
      }));
    },
  });

  const { data: recentViews, isLoading: loadingRecent } = useQuery({
    queryKey: ["analytics-recent-views"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_views")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Page Views
            </CardTitle>
            <Icon name="visibility" size={20} className="text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingViews ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold">{pageViews?.toLocaleString()}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Unique Visitors
            </CardTitle>
            <Icon name="person" size={20} className="text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingVisitors ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold">{uniqueVisitors?.toLocaleString()}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Projects Viewed
            </CardTitle>
            <Icon name="folder" size={20} className="text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingProjects ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold">{projectViews?.length || 0}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Views Over Time Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Page Views - Last 30 Days</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTimeline ? (
            <Skeleton className="h-[250px] w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={viewsOverTime}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  className="text-muted-foreground"
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  className="text-muted-foreground"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "hsl(var(--foreground))" }}
                />
                <Line
                  type="monotone"
                  dataKey="views"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6, fill: "hsl(var(--primary))" }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Projects Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Projects</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingProjects ? (
              <Skeleton className="h-[200px] w-full" />
            ) : projectViews?.length ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={projectViews} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} tickLine={false} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="count"
                    fill="hsl(var(--primary))"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground">No project views yet</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingRecent ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : recentViews?.length ? (
              <div className="space-y-3">
                {recentViews.slice(0, 5).map((view) => (
                  <div key={view.id} className="flex items-center justify-between text-sm">
                    <span className="truncate text-muted-foreground">
                      {view.page_path}
                    </span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                      {new Date(view.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No recent activity</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
